// LinkLauncherContext — opens the Link assistant (LinkWorkspace) as an overlay
// from ANYWHERE in the dashboard, optionally seeded with a first message or
// resuming a specific past chat.
//
// askLink() is the single "work on this with Link" entry point from an entity
// detail page (annons / modul / medarbetare). It:
//   1. sends the entity as a structured reference so Link has the real data,
//   2. seeds a short natural opener + a HIDDEN instruction telling Link to first
//      ask what the user wants to do (redigera / diskutera / förbättra) and act
//      on the answer — for editing it emits the entity as an editable artifact,
//   3. reuses an existing chat about the same entity if one exists (#225,
//      continue-or-new), else opens a fresh one.
// It replaces the older split of "Redigera med Link" + "Fråga Link".

import React, { Suspense, createContext, lazy, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatMention } from 'jasit-agent-kit/react';
import { employerChatApi, EmployerChat } from '@/lib/api/employerChatApi';
import { linkAgentApi, isSessionGoneError, isAuthExpiredError, type LinkAgentReference, type TurnWaitingInfo } from '@/lib/api/linkAgentApi';
import { useLinkAgent, linkSessionStore } from '@/contexts/LinkAgentProvider';
import { buildSeededText } from '@/lib/linkSeed';

const LinkWorkspace = lazy(() => import('@/components/dashboard/employer/chat/LinkWorkspace'));

interface AskLinkOpts {
    // The domain entity to reference. `type` matches the backend's reference
    // resolver ('need' | 'module' | 'employee'); `id` is the entity id
    // (the user_id for an employee).
    reference: LinkAgentReference;
    // Human display name (annons-roll / modulnamn / medarbetarnamn) — shown in
    // the reference breadcrumb + used as the #225 lookup / search label.
    refLabel: string;
    // The VISIBLE first message (natural opener naming the entity). The user
    // sees exactly this in their bubble.
    seed: string;
    // HIDDEN guidance appended to the seed telling Link how to act. Stripped
    // from the bubble; reaches the model. Defaults to a generic "ask what they
    // want" prompt when omitted.
    instruction?: string;
}

/** #301 — open Link with a STAGED (unsent) first message: the composer is
 *  pre-filled with an @-tagged prompt for the user to review + send. Contrast
 *  with askLink, which auto-sends a seed. Used by entity "Chatta med Link"
 *  buttons and the create-course "Få hjälp av Link" door. */
interface StageLinkOpts {
    // The suggested prompt text placed after the @-mention (or the whole message
    // when there's no entity to tag, e.g. course creation).
    prompt: string;
    // Optional entity to @-tag + attach as a structured reference.
    reference?: LinkAgentReference;
    // Display name for the @-mention token (the entity's role / name).
    label?: string;
}

/** #288 — a Link turn the user left running when they closed the window.
 *  'running' while it works, 'waiting' when it's parked needing the user
 *  (approval / question / continue), 'done'/'failed' once it terminates. */
export type BackgroundRun = {
    sessionId: string;
    state: 'running' | 'waiting' | 'done' | 'failed';
    waiting?: TurnWaitingInfo; // set only when state === 'waiting'
};

/** #302 — Link's introductory banner. 'full' is the first-ever greeting (names
 *  Nexilink + Link); 'short' is the on-demand recall that keeps only the "click
 *  the button and ask me…" nudge. */
export type WelcomeVariant = 'full' | 'short';

// #302 — the clickable question in the welcome banner. Clicking it opens Link
// with this text staged in the composer (see LinkLauncher).
export const WELCOME_QUESTION = 'Vad kan du hjälpa mig med?';

// #302 — banner copy for the two welcome variants. `lead` is the text BEFORE the
// clickable WELCOME_QUESTION so the FAB can render the question as a button.
export function welcomeCopy(variant: WelcomeVariant): { title: string; lead: string } {
    if (variant === 'full') {
        return {
            title: 'Välkommen till Nexilink!',
            lead: 'Jag heter Link och är din assistent. Klicka på knappen och fråga mig',
        };
    }
    return {
        title: 'Hej, jag är Link',
        lead: 'Klicka på knappen och fråga mig',
    };
}

interface LinkLauncherValue {
    /** Open Link as an overlay. Pass a seed to auto-send a first message, and
     *  optional structured references to attach to that first message. */
    openLink: (seed?: string, references?: LinkAgentReference[]) => void;
    /** Resume the most-recent Link chat (pinned-first), falling back to a fresh
     *  one if there are none. The launcher's default action (#292). */
    resumeLatest: () => void;
    /** Single entry point to work on an entity with Link (reference + ask-first
     *  instruction + #225 continue-or-new). */
    askLink: (opts: AskLinkOpts) => void;
    /** #301 — open Link with a STAGED (unsent) @-tagged prompt in the composer,
     *  ready for the user to review + send. */
    stageLink: (opts: StageLinkOpts) => void;
    /** Re-show the window over the ALREADY-resolved session (visibility only, no
     *  session re-resolution) — the stream persists in LinkAgentProvider across
     *  open/close, so reopening a live/current session must NOT reset it. */
    reopenLink: () => void;
    closeLink: () => void;
    /** #288 — request a close from OUTSIDE the chat surface (the floating Link
     *  button). Routes through the surface's own close path so an in-flight turn
     *  is backgrounded, not lost. */
    requestCloseLink: () => void;
    /** #288 — counter the surface watches to honour requestCloseLink. */
    closeSignal: number;
    isOpen: boolean;
    /** D3 — the Link window's current mode, published up by LinkWindow so the
     *  floating button can layer itself correctly: in 'windowed' mode Link (and
     *  its FAB) tuck BELOW the modal layer so dialogs/drawers come to the front;
     *  in 'fullscreen' the FAB rides above Link's blocking backdrop as the close
     *  button. Defaults to 'fullscreen' (LinkWindow's own default). */
    windowMode: 'fullscreen' | 'windowed';
    /** D3 — LinkWindow calls this whenever its mode changes. */
    setWindowMode: (mode: 'fullscreen' | 'windowed') => void;
    /** #288 — the current backgrounded run (or null). Drives the launcher badge. */
    backgroundRun: BackgroundRun | null;
    /** #288 — called when the user closes Link mid-turn. The stream now persists
     *  in LinkAgentProvider (frontend background mode), so this NO LONGER detaches
     *  server-side; it just records a fallback marker + polls for a done/failed
     *  cue. The FAB prefers LIVE provider state for running/waiting. */
    trackBackgroundRun: (sessionId: string) => void;
    /** #288 — reopen the backgrounded run (from the completion toast / badge). */
    openBackgroundRun: () => void;
    /** #288 — clear the backgrounded-run state (e.g. on launcher click). */
    dismissBackgroundRun: () => void;
    /** #302 — Link's intro banner (null when hidden). 'full' auto-shows on the
     *  first-ever visit; 'short' is the on-demand recall. */
    welcome: WelcomeVariant | null;
    /** #302 — show the welcome banner in the given variant (the FAB triggers the
     *  first-run 'full'; a mini "?" satellite re-shows 'short'). */
    showWelcome: (variant: WelcomeVariant) => void;
    /** #302 — dismiss the welcome banner. */
    dismissWelcome: () => void;
}

const LinkLauncherContext = createContext<LinkLauncherValue | null>(null);

export const useLinkLauncher = (): LinkLauncherValue => {
    const ctx = useContext(LinkLauncherContext);
    if (!ctx) throw new Error('useLinkLauncher must be used within a LinkLauncherProvider');
    return ctx;
};

const NOUN: Record<LinkAgentReference['type'], string> = {
    need: 'annonsen',
    module: 'modulen',
    employee: 'medarbetaren',
    application: 'ansökan',
};

// Human-readable Swedish labels for Link's gated tools. We NEVER surface the raw
// tool identifier (e.g. "invite_employees") to the user (#224 — hide internal
// names from Link UX); unknown tools fall back to a neutral "en åtgärd".
const TOOL_LABELS: Record<string, string> = {
    invite_employees: 'bjuda in medarbetare',
    assign_onboarding: 'tilldela onboarding',
    attach_courses_to_posting: 'koppla moduler till en annons',
    set_posting_status: 'ändra status på en annons',
    decide_application: 'hantera en ansökan',
    get_user_profile: 'hämta din profilinfo',
};
const friendlyTool = (name: string): string => TOOL_LABELS[name] || 'en åtgärd';

// #288 "Link needs you" — turn a parked background turn's waiting payload into
// the popup's Swedish copy. Exported so the FAB launcher renders the same banner.
export function waitingCopy(w?: TurnWaitingInfo): { title: string; detail?: string } {
    switch (w?.kind) {
        case 'ask_user':
            return { title: 'Link har en fråga', detail: w.summary };
        case 'approval': {
            const tools = w.tools ?? [];
            // Plan mode (`plan_ready`) isn't an "action" — it's Link presenting a
            // plan for sign-off before it starts. Give it its own copy.
            if (tools.includes('plan_ready')) {
                return { title: 'Link har en plan redo', detail: 'Link vill visa dig en plan innan den sätter igång.' };
            }
            const labels = tools.map(friendlyTool);
            return { title: 'Link vill utföra en åtgärd', detail: labels.length ? `Åtgärd: ${labels.join(', ')}` : undefined };
        }
        case 'continuation':
            return { title: 'Link vill fortsätta', detail: w.summary || 'Link har mer att göra och vill fortsätta.' };
        default:
            return { title: 'Link behöver din input', detail: w?.summary };
    }
}

// Session identity (seed / staged / resume / focus) now lives in
// LinkAgentProvider; the launcher OpenState collapses to pure visibility — no
// remount, so no nonce and no per-open payload.
type PendingChoice = { type: LinkAgentReference['type']; seededText: string; references: LinkAgentReference[]; matches: EmployerChat[] };

export const LinkLauncherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // The persistent stream/session owner. Openers resolve the session THROUGH
    // it, then this component just flips visibility.
    const linkAgent = useLinkAgent();
    // Visibility only — the stream persists in the provider across open/close.
    const [open, setOpen] = useState(false);
    // D3 — mirror of LinkWindow's window mode so the FAB can layer itself
    // relative to the modal stack (published up via setWindowMode).
    const [windowMode, setWindowMode] = useState<'fullscreen' | 'windowed'>('fullscreen');
    // When set, the "continue or new" dialog is shown before opening.
    const [pending, setPending] = useState<PendingChoice | null>(null);

    // #302 — Link's intro banner. Cleared whenever Link actually opens (it has
    // served its purpose the moment the user engages).
    const [welcome, setWelcome] = useState<WelcomeVariant | null>(null);
    const showWelcome = useCallback((variant: WelcomeVariant) => setWelcome(variant), []);
    const dismissWelcome = useCallback(() => setWelcome(null), []);

    const openLink = useCallback((seed?: string, references?: LinkAgentReference[]) => {
        setWelcome(null);
        linkAgent.openSession({ seed, seedReferences: references });
        setOpen(true);
    }, [linkAgent]);
    // #301 — open Link fresh with a STAGED (unsent) @-tagged prompt. Builds the
    // composer text as `@"Label" prompt` (or just the prompt when no entity), and
    // attaches the reference as a guaranteed structured ref on send.
    const stageLink = useCallback((opts: StageLinkOpts) => {
        setWelcome(null);
        const token = opts.label ? `${formatMention(opts.label)} ` : '';
        const stagedInput = `${token}${opts.prompt}`;
        linkAgent.openSession({
            stagedInput,
            stagedReferences: opts.reference ? [opts.reference] : undefined,
        });
        setOpen(true);
    }, [linkAgent]);
    // Resume an existing chat: focus the FIRST artifact (the entity the chat
    // started working on) rather than the most-recently emitted one.
    const openResume = useCallback((resumeChatId: string) => {
        setWelcome(null);
        linkAgent.openSession({ resumeChatId, focusFirstArtifact: true });
        setOpen(true);
    }, [linkAgent]);
    // Visibility-only reopen: show the window over whatever session the provider
    // already holds (no openSession → no stream reset). Tapping the FAB while a
    // live turn runs uses this so the running turn is never interrupted.
    const reopenLink = useCallback(() => setOpen(true), []);
    const closeLink = useCallback(() => setOpen(false), []);

    // #288 — external close request (the floating Link button). Bumping this
    // counter makes the open chat surface run its own close path (which
    // backgrounds an active turn) rather than the launcher yanking it away.
    const [closeSignal, setCloseSignal] = useState(0);
    const requestCloseLink = useCallback(() => setCloseSignal((n) => n + 1), []);

    // #288 "Run Link in the background" — closing the window no longer loses (or
    // detaches) an active turn: the stream persists in LinkAgentProvider. This
    // marker is a FALLBACK the poll below drives to a done/failed cue; the FAB
    // reads running/waiting from LIVE provider state. The cue is the floating Link
    // button (glow + a banner sliding out of it), NOT a separate toast — see
    // LinkLauncher.
    const [backgroundRun, setBackgroundRun] = useState<BackgroundRun | null>(null);
    // Ref mirror so openBackgroundRun can read the latest without a stale closure
    // or a side-effect-in-setState (which double-fires under StrictMode).
    const backgroundRunRef = useRef<BackgroundRun | null>(null);
    backgroundRunRef.current = backgroundRun;

    // #288 — on a HARD reload the provider is fresh (no live stream, no marker), so
    // the FAB would sit dead even though a turn is still running server-side. Read
    // the session the provider persisted while streaming and validate it once: if
    // it's still running/waiting, seed backgroundRun so the FAB glows/spins
    // immediately — before Link is reopened. The poll effect below then keeps it
    // current. Stale/finished ids are dropped so we don't re-check them next reload.
    useEffect(() => {
        const sid = linkSessionStore.load();
        if (!sid) return;
        const activeSid = sid;
        let cancelled = false;
        linkAgentApi.getTurnStatus(activeSid)
            .then((st) => {
                if (cancelled) return;
                if (st.state === 'running') {
                    // Still working — keep the key so every reload re-surfaces the spin.
                    setBackgroundRun({ sessionId: activeSid, state: 'running' });
                } else if (st.state === 'waiting') {
                    // Parked needing the user — keep the key; the blue cue persists
                    // across reloads until it's answered.
                    setBackgroundRun({ sessionId: activeSid, state: 'waiting', waiting: st.waiting });
                } else if (st.state === 'done' || st.state === 'failed') {
                    // Terminal — the run finished in the background while we were gone.
                    // Surface the green/red "klar / problem" cue ONCE, then drop the
                    // key so it doesn't re-appear on every subsequent reload.
                    setBackgroundRun({ sessionId: activeSid, state: st.state });
                    linkSessionStore.clear();
                } else {
                    // 'gone' (server evicted the session) or unknown — nothing to show.
                    linkSessionStore.clear();
                }
            })
            .catch((err) => {
                // #48 — a wiped session (server restart) 400s here. Drop the key so
                // we don't re-check a dead id on every reload. Any OTHER error is
                // transient — leave the key for the next load.
                if (isSessionGoneError(err)) linkSessionStore.clear();
            });
        return () => { cancelled = true; };
        // Once on mount — reload recovery only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const trackBackgroundRun = useCallback((sessionId: string) => {
        // #288 (frontend background mode): the stream now persists in
        // LinkAgentProvider even when the window is closed, so we NO LONGER detach
        // the turn server-side. We keep a lightweight marker as a FALLBACK cue for
        // the FAB, but the FAB prefers LIVE provider state (see LinkLauncher).
        setBackgroundRun({ sessionId, state: 'running' });
    }, []);
    const openBackgroundRun = useCallback(() => {
        // Read from the ref, not a state-updater side effect — calling openResume
        // inside a setState updater double-fires under StrictMode and races the
        // resume. Snapshot the id, clear the cue, then open.
        const sid = backgroundRunRef.current?.sessionId;
        setBackgroundRun(null);
        if (sid) openResume(sid);
    }, [openResume]);
    const dismissBackgroundRun = useCallback(() => setBackgroundRun(null), []);

    // Launcher default (#292): resume the most-recent chat (list is pinned-first,
    // then most-recently-active), or a fresh one if there are none.
    const resumeLatest = useCallback(async () => {
        try {
            const chats = await employerChatApi.list();
            const latest = chats?.[0];
            if (latest?.id) { openResume(latest.id); return; }
        } catch {
            /* fall through to a fresh chat */
        }
        openLink();
    }, [openLink, openResume]);

    // Poll the backgrounded run while it's active (running OR parked waiting for
    // input). Surfaces the "waiting" cue live, toasts on done/failed, stops on a
    // terminal state. setBackgroundRun uses identity-preserving updates so an
    // unchanged status doesn't churn this effect's interval.
    useEffect(() => {
        if (!backgroundRun || (backgroundRun.state !== 'running' && backgroundRun.state !== 'waiting')) return;
        const sid = backgroundRun.sessionId;
        let stopped = false;
        const poll = async () => {
            try {
                const st = await linkAgentApi.getTurnStatus(sid);
                if (stopped) return;
                if (st.state === 'waiting') {
                    // Parked, needs the user — keep polling, surface the cue.
                    setBackgroundRun((cur) =>
                        cur && cur.state === 'waiting' && cur.waiting?.summary === st.waiting?.summary && cur.waiting?.kind === st.waiting?.kind
                            ? cur
                            : { sessionId: sid, state: 'waiting', waiting: st.waiting });
                    return;
                }
                if (st.running) {
                    // Back to actively working (e.g. answered elsewhere).
                    setBackgroundRun((cur) => (cur && cur.state === 'running') ? cur : { sessionId: sid, state: 'running' });
                    return;
                }
                if (st.state === 'done') {
                    // Terminal — the FAB turns green and slides out a "Link är
                    // klar" banner. No toast.
                    setBackgroundRun({ sessionId: sid, state: 'done' });
                } else if (st.state === 'failed') {
                    // Terminal — the FAB turns red and slides out an error banner.
                    setBackgroundRun({ sessionId: sid, state: 'failed' });
                } else {
                    // 'gone' — the session was wiped (server restart). Stop quietly.
                    setBackgroundRun(null);
                }
            } catch (err) {
                // #48 — the status endpoint 400s on a WIPED session (server
                // restart): assertOwnership can't find the in-process session, so
                // it never reaches the graceful state:"gone" path. Treat that as
                // terminal — clear the persisted marker + the cue so the 4s poll
                // stops hammering a dead id (this was the endless /status 400 loop).
                // Recovery (auto-resume) is driven by the provider's onError the
                // next time the user interacts. Any OTHER error is transient — keep
                // polling.
                if (isSessionGoneError(err)) {
                    linkSessionStore.clear();
                    setBackgroundRun(null); // flips the effect's guard → interval stops
                } else if (isAuthExpiredError(err)) {
                    // #308 — the user logged out (or the refresh token expired) while
                    // a run was still tracked. "Any OTHER error is transient" was true
                    // for network blips and false for this: the api client has already
                    // failed a refresh and cleared the tokens, so every subsequent poll
                    // is a guaranteed 401 — and each one fired another "Session
                    // Expired" toast, giving a fresh one every 4 seconds on the login
                    // screen, forever.
                    //
                    // Only reachable with an ACTIVE background run, which is why
                    // logging out of an idle app never showed it.
                    //
                    // The session marker is deliberately KEPT: the turn may well still
                    // be running server-side, and the next login should re-surface it.
                    // It is the polling that must stop, not the memory of the run.
                    setBackgroundRun(null);
                }
            }
        };
        const iv = setInterval(poll, 4000);
        poll();
        return () => { stopped = true; clearInterval(iv); };
    }, [backgroundRun]);

    // A TERMINAL (done/failed) background-run marker is a cue for when Link is
    // CLOSED ("your run finished" — green FAB + banner). The moment Link is opened
    // by ANY path (banner button, FAB tap, ⌘K, an entity button, the history
    // switcher, resume-latest, or the run finishing while already open) that cue
    // has served its purpose, so clear it — otherwise the green banner slides back
    // out on the next close. The blue "needs you" (state==='waiting') is NOT
    // cleared here: it's a live pending gate that must persist until answered.
    useEffect(() => {
        if (open && backgroundRun && (backgroundRun.state === 'done' || backgroundRun.state === 'failed')) {
            setBackgroundRun(null);
        }
    }, [open, backgroundRun]);

    const askLink = useCallback(async (opts: AskLinkOpts) => {
        const seededText = buildSeededText(opts);
        const references = [opts.reference];
        // #225 continue-or-new: is there already a chat about this exact entity?
        const searchMarker = `${opts.reference.type}_id=${opts.reference.id}`;
        try {
            const matches = await employerChatApi.listReferencing(searchMarker);
            if (matches && matches.length > 0) {
                setPending({ type: opts.reference.type, seededText, references, matches });
                return;
            }
        } catch {
            // Lookup failed — fall through to a fresh seeded chat.
        }
        openLink(seededText, references);
    }, [openLink]);

    const noun = pending ? NOUN[pending.type] : 'den';

    return (
        <LinkLauncherContext.Provider value={{ openLink, resumeLatest, askLink, stageLink, reopenLink, closeLink, requestCloseLink, closeSignal, isOpen: open, windowMode, setWindowMode, backgroundRun, trackBackgroundRun, openBackgroundRun, dismissBackgroundRun, welcome, showWelcome, dismissWelcome }}>
            {children}

            {/* Continue-or-new dialog (#225) — shown when an existing chat already
                references this entity. */}
            <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Fortsätt på en tidigare chatt?</DialogTitle>
                        <DialogDescription>
                            Du har redan {pending && pending.matches.length > 1 ? `${pending.matches.length} chattar` : 'en chatt'} om {noun}. Vill du fortsätta där du var, eller starta en ny?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { const p = pending; setPending(null); if (p) openLink(p.seededText, p.references); }}
                        >
                            Starta en ny chatt
                        </Button>
                        <Button
                            onClick={() => { const id = pending?.matches[0]?.id; setPending(null); if (id) openResume(id); }}
                        >
                            Fortsätt
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* #288 "Link needs you" is now surfaced by the FAB launcher
                (LinkLauncher), which slides a banner out of the floating button
                so the cue lives in one place regardless of the current page. */}

            {open && (
                <Suspense fallback={null}>
                    <LinkWorkspace
                        onClose={closeLink}
                        onBackgroundRun={trackBackgroundRun}
                        closeSignal={closeSignal}
                    />
                </Suspense>
            )}
        </LinkLauncherContext.Provider>
    );
};
